import { BrowserModule, HammerModule } from '@angular/platform-browser';
import { NgModule, Injectable, ErrorHandler, inject, provideAppInitializer } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

// Global Components

import { AppComponent } from '@components/root/app.component';
import { HeaderComponent } from '@components/header/header.component';
import { LoaderComponent } from '@components/loader/loader.component';
import { FooterComponent } from '@components/footer/footer.component';

// Routing Module
// We have to import a Custom Routing Module from another module designed for that.

import { RoutingModule } from './routing.module';

// Services - aka providers
// Used to communicate components

import { DataService } from '@services/data.service';
import { ApiService } from '@services/api.service';
import { LogService } from '@services/log.service';
import { ConfigService } from '@services/config.service';
import { ConnectionService } from 'ng-connection-service';

// Plugins

import { RoundProgressModule } from 'angular-svg-round-progressbar';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { NgxsModule } from '@ngxs/store';
import { NgxsSelectSnapshotModule } from '@ngxs-labs/select-snapshot';

// Angular Material

import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NewUpdateComponent } from './dialogs/new-update/new-update.component';
import { AccessCodeComponent } from './components/access-code/access-code.component';
import { CognosService } from '@services/cognos.service';
import { CookiesExpiredComponent } from './dialogs/cookies-expired/cookies-expired.component';

// AoT requires an exported function for factories
export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

import { HammerGestureConfig, HAMMER_GESTURE_CONFIG } from '@angular/platform-browser';
import { AuthInterceptor } from '@services/http-interceptor';
import { APP_BASE_HREF } from '@angular/common';
import { OnlyNumbers } from './directives/only-numbers.directive';
import { SharedModule } from '@modules/shared/shared.module';
import environment from 'environments/environment';

/** Store States */
import { ConfigState } from '@store/config.state';

// Import version from config for hash versioning
import configJson from '../assets/config_common.json';
import { GlobalErrorHandler } from './classes/globalErrorHandler';
import { EventHint, init } from '@sentry/browser';

declare var Hammer: any;


@Injectable()
export class MyHammerConfig extends HammerGestureConfig {
  overrides = <any> {
    'swipe': { direction: Hammer.DIRECTION_HORIZONTAL }
  };
  buildHammer(element: HTMLElement) {
    return new Hammer(element, {
      touchAction: 'pan-y',
    });
  }
}


@NgModule({ declarations: [
        AppComponent,
        HeaderComponent,
        LoaderComponent,
        FooterComponent,
        NewUpdateComponent,
        AccessCodeComponent,
        CookiesExpiredComponent,
        OnlyNumbers
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        RoutingModule,
        BrowserAnimationsModule,
        SharedModule,
        HammerModule,
        NgxsModule.forRoot([
            ConfigState
        ], {
            developmentMode: !environment.production
        }),
        NgxsSelectSnapshotModule.forRoot(),
        // Plugins
        RoundProgressModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: (createTranslateLoader),
                deps: [HttpClient],
            }
        })

    ], providers: [
        DataService,
        ApiService,
        LogService,
        ConnectionService,
        ConfigService,
        CognosService,
        AuthInterceptor,
        {
            provide: HAMMER_GESTURE_CONFIG,
            useClass: MyHammerConfig
        },
        { provide: ErrorHandler,
            useClass: GlobalErrorHandler
        },
        // Angular’s inject inside provideAppInitializer automatically resolves all requested dependencies when using the factory form above. No manual IIFE needed.
        provideAppInitializer(() => {
            const configService = inject(ConfigService);
            // We only need ConfigService for load(), ignore the others here
            return configService.load();
        // provideAppInitializer(() => {
        // const initializerFn = ((configService: ConfigService) => () => configService.load())(inject(ConfigService), inject(CognosService), inject(ApiService));
        // return initializerFn();
      }),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true
        },
        {
            provide: APP_BASE_HREF,
            useValue: '/'
        },
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule {
    constructor(private logger: LogService) {
        console.log("=================================================")
        console.log("Shift+X to show state, Shift+D to set loglvl to *")
        console.log("=================================================")
        this.logger.info("AppModule", "init", "AppModule initialized");
    }
}